
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Shield, Loader2, CheckCircle2, Anchor, Ship, Navigation, Globe } from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import { useInventaire } from '@/hooks/useInventaire';
import { useTranslation } from 'react-i18next';

interface Division240SelectorProps {
  selectedBateauId: string;
  onEquipmentAdded: () => void;
}

// Note: `label` values are persisted (sous_categorie in DB) and used for matching in
// Division240Reminder. Keep them in French. `i18nKey` is only used for display translation.
const CATEGORIES_NAVIGATION = [
  {
    id: 'basique',
    label: 'Basique',
    i18nKey: 'basique',
    icon: Anchor,
    color: 'bg-green-100 text-green-700',
  },
  {
    id: 'cotier',
    label: 'Côtier',
    i18nKey: 'cotier',
    icon: Ship,
    color: 'bg-blue-100 text-blue-700',
  },
  {
    id: 'semi-hauturier',
    label: 'Semi-hauturier',
    i18nKey: 'semiHauturier',
    icon: Navigation,
    color: 'bg-orange-100 text-orange-700',
  },
  {
    id: 'hauturier',
    label: 'Hauturier',
    i18nKey: 'hauturier',
    icon: Globe,
    color: 'bg-red-100 text-red-700',
  },
];

// Référence : Division 240 — arrêtés du 06/05/19, 11/10/24 et 21.05.26
// `nom` values are persisted as-is to the database (article name) and must stay in French.
// `key` is only used to look up the translated display label (division240Selector.equipmentList).
interface Equipement { nom: string; key: string }

const EQUIPEMENTS_BASIQUE: Equipement[] = [
  { nom: 'Équipement individuel de flottabilité (1 par personne)', key: '0' },
  { nom: 'Dispositif lumineux individuel (lampe flash, torche ou cyalume)', key: '1' },
  { nom: 'Extincteur(s) portatif(s) d\'incendie (annexe 240-A.3)', key: '2' },
  { nom: 'Dispositif d\'assèchement manuel (écope, seau ou pompe à main)', key: '3' },
  { nom: 'Dispositif de remorquage (point d\'amarrage + bout)', key: '4' },
  { nom: 'Ligne de mouillage adaptée au navire', key: '5' },
  { nom: 'Annuaire des marées (heures et coefficients — non requis en Méditerranée)', key: '6' },
  { nom: 'Pavillon national (hors eaux territoriales)', key: '7' },
];

const EQUIPEMENTS_COTIER: Equipement[] = [
  { nom: 'Bouée fer à cheval ou couronne (dispositif de repérage MOB)', key: '8' },
  { nom: '3 feux rouges à main (division 311)', key: '9' },
  { nom: 'Compas magnétique de route, étanche et éclairé', key: '10' },
  { nom: 'Cartes marines officielles à jour (papier ou électronique)', key: '11' },
  { nom: 'RIPAM — Règlement international pour prévenir les abordages en mer', key: '12' },
  { nom: 'Document décrivant le système de balisage de la zone', key: '13' },
];

const EQUIPEMENTS_SEMI_HAUTURIER: Equipement[] = [
  { nom: 'Installation radioélectrique VHF fixe', key: '14' },
  { nom: 'Radeau(x) de survie gonflable(s) pour toutes les personnes à bord', key: '15' },
  { nom: 'Matériel pour faire le point, tracer et suivre une route', key: '16' },
  { nom: 'Livre des feux tenu à jour (papier ou électronique)', key: '17' },
  { nom: 'Journal de bord tenu à jour', key: '18' },
  { nom: 'Dispositif de réception des prévisions météorologiques marines', key: '19' },
  { nom: 'Harnais et longe (1 par personne sur voiliers, 1 à bord sinon)', key: '20' },
  { nom: 'Trousse de secours conforme à l\'article 240-2.19', key: '21' },
  { nom: 'Projecteur de recherche portatif ou fixe, étanche', key: '22' },
];

const EQUIPEMENTS_HAUTURIER: Equipement[] = [
  { nom: 'Radiobalise de localisation des sinistres (RLS / EPIRB)', key: '23' },
  { nom: 'Émetteur-récepteur VHF portatif et étanche', key: '24' },
  { nom: 'Radeau de survie normé (ISO 9650 type I, classe II div. 333, ou div. 311)', key: '25' },
  { nom: 'Recommandé : dispositif de communication par satellite (CCMM / MRCC)', key: '26' },
];

const getEquipementsPourCategorie = (categorieId: string): Equipement[] => {
  switch (categorieId) {
    case 'basique':
      return [...EQUIPEMENTS_BASIQUE];
    case 'cotier':
      return [...EQUIPEMENTS_BASIQUE, ...EQUIPEMENTS_COTIER];
    case 'semi-hauturier':
      return [...EQUIPEMENTS_BASIQUE, ...EQUIPEMENTS_COTIER, ...EQUIPEMENTS_SEMI_HAUTURIER];
    case 'hauturier':
      return [...EQUIPEMENTS_BASIQUE, ...EQUIPEMENTS_COTIER, ...EQUIPEMENTS_SEMI_HAUTURIER, ...EQUIPEMENTS_HAUTURIER];
    default:
      return [];
  }
};

const Division240Selector = ({ selectedBateauId, onEquipmentAdded }: Division240SelectorProps) => {
  const { t } = useTranslation('inventory');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const { ajouterPlusieursArticles, articles } = useInventaire();

  const equipements = selectedCategory ? getEquipementsPourCategorie(selectedCategory) : [];

  const handleAjouterEquipements = async () => {
    if (!selectedCategory || !selectedBateauId) return;

    setLoading(true);
    try {
      // Filter out equipment that already exists in inventory for this boat
      const articlesExistants = articles
        .filter(a => a.bateau_id === selectedBateauId && a.categorie === 'Sécurité')
        .map(a => a.nom.toLowerCase());

      const nouveauxEquipements = equipements.filter(
        eq => !articlesExistants.includes(eq.nom.toLowerCase())
      );

      if (nouveauxEquipements.length === 0) {
        toast({
          title: t('division240Selector.alreadyUpToDateTitle'),
          description: t('division240Selector.alreadyUpToDateDescription'),
        });
        setLoading(false);
        return;
      }

      const getQuantiteMax = (nom: string): number => {
        const nomLower = nom.toLowerCase();
        if (nomLower.includes('équipement individuel de flottabilité')) return 8;
        if (nomLower.includes('feux rouges à main')) return 3;
        if (nomLower.includes('harnais')) return 2;
        return 1;
      };

      const articlesData = nouveauxEquipements.map(({ nom }) => ({
        nom,
        categorie: 'Sécurité',
        sous_categorie: `Division 240 - ${CATEGORIES_NAVIGATION.find(c => c.id === selectedCategory)?.label}`,
        quantite: 0,
        quantite_max: getQuantiteMax(nom),
        unite: 'unité',
        description: `Équipement obligatoire Division 240 - Navigation ${CATEGORIES_NAVIGATION.find(c => c.id === selectedCategory)?.label}`,
        notes: null,
        prix_unitaire: null,
        date_achat: null,
        date_peremption: null,
        espace_stockage_id: null,
        bateau_id: selectedBateauId,
      }));

      const result = await ajouterPlusieursArticles(articlesData, selectedBateauId);

      if (result.error) {
        toast({
          title: t('division240Selector.error'),
          description: t('division240Selector.cannotAddEquipment'),
          variant: 'destructive',
        });
      } else {
        toast({
          title: t('division240Selector.equipmentAddedTitle'),
          description: t('division240Selector.equipmentAddedDescription', { count: nouveauxEquipements.length }),
        });
        onEquipmentAdded();
      }
    } catch (err) {
      console.error('Erreur:', err);
      toast({
        title: t('division240Selector.error'),
        description: t('division240Selector.unexpectedError'),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-nautic">
          <Shield className="h-5 w-5" />
          {t('division240Selector.title')}
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          {t('division240Selector.subtitle')}
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        <RadioGroup value={selectedCategory} onValueChange={setSelectedCategory} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {CATEGORIES_NAVIGATION.map(cat => {
            const Icon = cat.icon;
            return (
              <Label
                key={cat.id}
                htmlFor={`nav-${cat.id}`}
                className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  selectedCategory === cat.id
                    ? 'border-nautic bg-nautic/5'
                    : 'border-border hover:border-nautic/40'
                }`}
              >
                <RadioGroupItem value={cat.id} id={`nav-${cat.id}`} />
                <div className={`p-2 rounded-lg ${cat.color}`}>
                  <Icon className="h-5 w-5" />
                </div>
                <div>
                  <p className="font-semibold">{t(`division240Selector.${cat.i18nKey}`)}</p>
                  <p className="text-xs text-muted-foreground">{t(`division240Selector.${cat.i18nKey}Description`)}</p>
                </div>
              </Label>
            );
          })}
        </RadioGroup>

        {selectedCategory && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold text-sm text-nautic">
                {t('division240Selector.requiredEquipment', { count: equipements.length })}
              </h4>
              <Badge variant="outline" className="text-xs">
                {t(`division240Selector.${CATEGORIES_NAVIGATION.find(c => c.id === selectedCategory)?.i18nKey}`)}
              </Badge>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-60 overflow-y-auto">
              {equipements.map((eq, i) => {
                const dejaPresent = articles.some(
                  a => a.bateau_id === selectedBateauId && a.nom.toLowerCase() === eq.nom.toLowerCase() && a.categorie === 'Sécurité'
                );
                return (
                  <div
                    key={i}
                    className={`flex items-center gap-2 p-2 rounded-md text-sm ${
                      dejaPresent ? 'bg-green-50 text-green-700' : 'bg-muted/50'
                    }`}
                  >
                    {dejaPresent ? (
                      <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />
                    ) : (
                      <Shield className="h-4 w-4 text-muted-foreground shrink-0" />
                    )}
                    <span>{t(`division240Selector.equipmentList.${eq.key}`)}</span>
                  </div>
                );
              })}
            </div>

            <Button
              onClick={handleAjouterEquipements}
              disabled={loading}
              className="w-full bg-nautic-gold text-nautic hover:bg-nautic-lightgold font-semibold"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('division240Selector.addingInProgress')}
                </>
              ) : (
                <>
                  <Shield className="h-4 w-4 mr-2" />
                  {t('division240Selector.addMissingEquipment')}
                </>
              )}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default Division240Selector;
